import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Card, PageHeader, Pagination, Select, Table, type Tone } from '../../design';
import { fmtDateTime } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import { useOrgOptions } from '../users/UsersPage';

type Delivery = components['schemas']['NotificationDelivery'];
type Status = components['schemas']['DeliveryStatus'];
type Channel = components['schemas']['NotificationChannel'];

export const DELIVERY_STATUS: Record<Status, { label: string; tone: Tone }> = {
  queued: { label: 'În coadă', tone: 'blue' },
  sent: { label: 'Trimisă', tone: 'green' },
  failed: { label: 'Eșuată', tone: 'red' },
  skipped: { label: 'Omisă', tone: 'neutral' },
};
const TEMPLATE_LABEL: Record<string, string> = {
  'access-code': 'Cod de acces',
  reminder: 'Reamintire',
  alert: 'Alertă',
  event: 'Eveniment',
  test: 'Test canal',
  'rule-test': 'Test regulă',
};

/** Jurnalul livrărilor (e-mail / SMS): destinatarul e mascat, corpul mesajului nu se păstrează. */
export function DeliveriesPage() {
  const { user } = useAuth();
  const { values, page, pageSize, set } = useUrlState(['status', 'channel', 'orgId'] as const);
  const orgs = useOrgOptions();
  const query = useQuery({
    queryKey: ['notification-deliveries', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/notification-deliveries', {
          params: {
            query: queryParams({
              status: (values.status || undefined) as Status | undefined,
              channel: (values.channel || undefined) as Channel | undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
    refetchInterval: 15_000,
  });
  return (
    <>
      <PageHeader
        title="Livrări"
        subtitle="Notificările trimise prin e-mail și SMS: stare, încercări, erori. Destinatarul apare mascat; codurile de acces nu se păstrează."
      />
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Select
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            style={{ maxWidth: 180 }}
            aria-label="Stare"
          >
            <option value="">Toate stările</option>
            {(Object.keys(DELIVERY_STATUS) as Status[]).map((st) => (
              <option key={st} value={st}>
                {DELIVERY_STATUS[st].label}
              </option>
            ))}
          </Select>
          <Select
            value={values.channel}
            onChange={(e) => set({ channel: e.target.value })}
            style={{ maxWidth: 160 }}
            aria-label="Canal"
          >
            <option value="">Toate canalele</option>
            <option value="email">E-mail</option>
            <option value="sms">SMS</option>
          </Select>
          {user?.orgId === null && (
            <Select
              value={values.orgId}
              onChange={(e) => set({ orgId: e.target.value })}
              style={{ maxWidth: 240 }}
              aria-label="Organizație"
            >
              <option value="">Toate organizațiile</option>
              {orgs.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        <Table<Delivery>
          columns={[
            {
              key: 'at',
              header: 'Creată',
              render: (d) => <span className="muted">{fmtDateTime(d.createdAt)}</span>,
            },
            {
              key: 'channel',
              header: 'Canal',
              render: (d) => (
                <Badge tone={d.channel === 'sms' ? 'purple' : 'blue'}>
                  {d.channel === 'sms' ? 'SMS' : 'E-mail'}
                </Badge>
              ),
            },
            {
              key: 'to',
              header: 'Destinatar',
              render: (d) => <span className="mono">{d.recipientMasked}</span>,
            },
            {
              key: 'template',
              header: 'Mesaj',
              render: (d) => TEMPLATE_LABEL[d.template] ?? d.template,
            },
            {
              key: 'related',
              header: 'Legat de',
              render: (d) =>
                d.relatedType === 'operation' && d.relatedId ? (
                  <Link to={`/operatiuni/${d.relatedId}`}>operațiune</Link>
                ) : d.relatedType === 'alert' ? (
                  <Link to="/alerte">alertă</Link>
                ) : (
                  <span className="muted">{d.relatedType ?? '—'}</span>
                ),
            },
            {
              key: 'status',
              header: 'Stare',
              render: (d) => (
                <>
                  <Badge tone={DELIVERY_STATUS[d.status].tone}>
                    {DELIVERY_STATUS[d.status].label}
                  </Badge>
                  {d.lastError && (
                    <div className="small muted" style={{ maxWidth: 320 }}>
                      {d.lastError}
                    </div>
                  )}
                </>
              ),
            },
            { key: 'attempts', header: 'Încercări', align: 'right', render: (d) => d.attempts },
            {
              key: 'sent',
              header: 'Trimisă',
              render: (d) => (
                <span className="muted">{d.sentAt ? fmtDateTime(d.sentAt) : '—'}</span>
              ),
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(d) => d.id}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Nicio livrare pentru filtrele alese.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
    </>
  );
}
